import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { requestCurrentUser, updateUser } from "../../../actions/user";
import { setGlobalAlert } from "../../../actions/common";
import {
  hasUsableStaffName,
  needsStaffSetupPrompt,
} from "../../../helpers/staffName";
import {
  hasStaffPinUserSet,
  normalizeStaffPin,
  staffPinError,
  STAFF_PIN_LEN,
  STAFF_PIN_MAX_LEN,
  STAFF_PIN_MIN_LEN,
} from "../../../helpers/staffPin";

const StaffNameDialog = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s?.currentUser?.user || {});
  const [fullName, setFullName] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const open = needsStaffSetupPrompt(currentUser);
  const needsName = !hasUsableStaffName(currentUser);
  const needsPin = !hasStaffPinUserSet(currentUser);

  useEffect(() => {
    if (!open) return;
    const existing = String(currentUser?.displayName || "").trim();
    const email = String(currentUser?.email || "").trim().toLowerCase();
    if (existing && existing.toLowerCase() !== email && !existing.includes("@")) {
      setFullName(existing);
    } else {
      setFullName("");
    }
    setPin("");
    setConfirmPin("");
    setError("");
  }, [open, currentUser?.uid]);

  if (!open) return null;

  const handleSave = () => {
    const payload = {};

    if (needsName) {
      const name = fullName.trim().replace(/\s+/g, " ");
      const parts = name.split(" ").filter(Boolean);
      if (parts.length < 2) {
        setError("Please enter your first and last name.");
        return;
      }
      payload.displayName = name;
    }

    if (needsPin) {
      const pinErr = staffPinError(pin, { confirmPin });
      if (pinErr) {
        setError(pinErr);
        return;
      }
      // Overwrites any provisional auto-assigned PIN; backend sets staffPinUserSet.
      payload.staffPin = normalizeStaffPin(pin);
    }

    setSaving(true);
    setError("");
    dispatch(updateUser(payload))
      .then((res) => {
        if (res?.error) {
          setError(
            res?.payload?.response?.data?.message ||
              res?.error?.message ||
              "Could not save. Try again."
          );
          return;
        }
        dispatch(requestCurrentUser());
        dispatch(
          setGlobalAlert({
            type: "success",
            message: needsPin
              ? "Thanks. Your name and Team Member Pin are saved."
              : "Thanks. Your name is saved.",
          })
        );
      })
      .catch(() => {
        setError("Could not save. Try again.");
      })
      .finally(() => setSaving(false));
  };

  const canContinue =
    (!needsName || !!fullName.trim()) &&
    (!needsPin ||
      (normalizeStaffPin(pin).length >= STAFF_PIN_MIN_LEN &&
        normalizeStaffPin(confirmPin).length >= STAFF_PIN_MIN_LEN));

  const title =
    needsName && needsPin
      ? "Set up your profile"
      : needsPin
        ? "Create your Team Member Pin"
        : "What’s your name?";

  return (
    <Dialog
      open
      disableEscapeKeyDown
      fullWidth
      maxWidth="xs"
      aria-labelledby="staff-name-dialog-title"
    >
      <DialogTitle id="staff-name-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <p style={{ margin: "0 0 16px", color: "#667085", fontSize: 14 }}>
          {needsName && needsPin
            ? "Your team uses your name on orders, and your Team Member Pin so we know who’s working. Both are required to continue."
            : needsPin
              ? "Choose a unique Team Member Pin so your team knows who’s working. It must be unique on this account."
              : "Your team uses this so they know who’s working on orders. Enter your full name to continue."}
        </p>
        {needsName && (
          <TextField
            autoFocus
            fullWidth
            label="Name"
            placeholder="Jane Smith"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
            }}
            error={!!error && !needsPin}
            helperText={
              error && !needsPin ? error : "First and last name"
            }
            disabled={saving}
            sx={{ mb: needsPin ? 2 : 0 }}
          />
        )}
        {needsPin && (
          <>
            <TextField
              autoFocus={!needsName}
              fullWidth
              label="Team Member Pin"
              placeholder={`${STAFF_PIN_LEN} digits`}
              value={pin}
              onChange={(e) =>
                setPin(normalizeStaffPin(e.target.value).slice(0, STAFF_PIN_MAX_LEN))
              }
              inputProps={{
                inputMode: "numeric",
                autoComplete: "new-password",
                maxLength: STAFF_PIN_MAX_LEN,
              }}
              type="password"
              disabled={saving}
              sx={{ mb: 2 }}
              helperText={`Unique to your shop · ${STAFF_PIN_MIN_LEN}–${STAFF_PIN_MAX_LEN} digits`}
            />
            <TextField
              fullWidth
              label="Confirm Team Member Pin"
              value={confirmPin}
              onChange={(e) =>
                setConfirmPin(
                  normalizeStaffPin(e.target.value).slice(0, STAFF_PIN_MAX_LEN)
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSave();
                }
              }}
              inputProps={{
                inputMode: "numeric",
                autoComplete: "new-password",
                maxLength: STAFF_PIN_MAX_LEN,
              }}
              type="password"
              error={!!error}
              helperText={error || "Re-enter your Team Member Pin"}
              disabled={saving}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={saving || !canContinue}
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffNameDialog;
